import React, { useState, useEffect, useCallback } from "react";
import { useVisitedPageCache, visitedPageKey } from "../../utils/visitedPageCache";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  erpFetch,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  getActiveBranchId,
} from "../../erpClient";
import Pagination from "../../components/Pagination";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { formatIndianRupee } from "../../utils/formatLiveRate";

const METAL_TYPES = ["GOLD", "SILVER", "NONE"];
const VALUE_MODES = ["AMOUNT", "GOLD_WEIGHT"];
const BONUS_TYPES = ["FREE_MONTH", "PERCENT", "NONE"];
const INSTALLMENT_TYPES = ["FIXED", "FLEXIBLE"];

const EMPTY_FORM = {
  name: "",
  code: "",
  metal_type: "GOLD",
  value_mode: "AMOUNT",
  duration_months: "",
  waiting_months: "",
  bonus_type: "NONE",
  bonus_months: "",
  bonus_percent: "",
  installment_type: "FIXED",
  default_amount: "",
  min_amount: "",
  max_amount: "",
  late_fee_amount: "",
  is_mandal: 0,
  status: 1,
};

function SchemePlan() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.SCHEME_PLAN_VIEW);
  const canAdd = hasPermission(PERMISSIONS.SCHEME_PLAN_ADD);
  const canEdit = hasPermission(PERMISSIONS.SCHEME_PLAN_EDIT);

  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const pageCache = useVisitedPageCache();

  const fetchList = useCallback(async (force = false) => {
    const branchId = getActiveBranchId() || 1;
    const cacheKey = visitedPageKey([
      branchId,
      debouncedSearch,
      pagination.page,
      pagination.limit,
    ]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached && canView) {
      setList(cached.items);
      setPagination((p) => ({ ...p, total: cached.total }));
      setLoading(false);
      return;
    }
    if (!canView) return;
    setLoading(true);
    try {
      const res = await erpFetch("/scheme_plan", {
        method: "POST",
        body: {
          type: "list",
          search: debouncedSearch,
          status: 1,
          page: pagination.page,
          limit: pagination.limit,
          branch_id: branchId,
        },
      });
      if (isErpApiSuccess(res)) {
        const items = res.data?.items ?? res.data ?? [];
        const total = res.data?.pagination?.total ?? 0;
        setList(items);
        if (res.data?.pagination) {
          setPagination((p) => ({ ...p, total }));
        }
        pageCache.write(cacheKey, { items, total });
      } else {
        toast.error(res?.message || "Failed to load scheme plans.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to load scheme plans."));
    } finally {
      setLoading(false);
    }
  }, [canView, debouncedSearch, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openAdd = () => {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      name: row.name ?? "",
      code: row.code ?? "",
      metal_type: row.metal_type ?? "GOLD",
      value_mode: row.value_mode ?? "AMOUNT",
      duration_months: row.duration_months ?? "",
      waiting_months: row.waiting_months ?? "",
      bonus_type: row.bonus_type ?? "NONE",
      bonus_months: row.bonus_months ?? "",
      bonus_percent: row.bonus_percent ?? "",
      installment_type: row.installment_type ?? "FIXED",
      default_amount: row.default_amount ?? "",
      min_amount: row.min_amount ?? "",
      max_amount: row.max_amount ?? "",
      late_fee_amount: row.late_fee_amount ?? "",
      is_mandal: Number(row.is_mandal) === 1 ? 1 : 0,
      status: row.status ?? 1,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!form.duration_months) {
      toast.error("Duration (months) is required.");
      return;
    }
    if (Number(form.is_mandal) === 1 && !(Number(form.late_fee_amount) > 0)) {
      toast.error("Late fee amount is required for Mandal plans.");
      return;
    }
    setSaving(true);
    try {
      const branchId = getActiveBranchId() || 1;
      const payload = {
        name: form.name,
        code: form.code,
        metal_type: form.metal_type,
        value_mode: form.value_mode,
        bonus_type: form.bonus_type,
        installment_type: form.installment_type,
        branch_id: branchId,
        duration_months: Number(form.duration_months) || undefined,
        waiting_months:
          form.waiting_months !== "" ? Number(form.waiting_months) : 0,
        bonus_months: form.bonus_months !== "" ? Number(form.bonus_months) : 0,
        bonus_percent: form.bonus_percent !== "" ? Number(form.bonus_percent) : 0,
        default_amount: form.default_amount !== "" ? Number(form.default_amount) : undefined,
        min_amount: form.min_amount !== "" ? Number(form.min_amount) : 0,
        max_amount: form.max_amount !== "" ? Number(form.max_amount) : 0,
        late_fee_amount:
          form.late_fee_amount !== "" ? Number(form.late_fee_amount) : 0,
        is_mandal: Number(form.is_mandal) === 1 ? 1 : 0,
        status: Number(form.status),
      };

      if (editRow) {
        payload.type = "update";
        payload.id = editRow.id;
      } else {
        payload.type = "create";
      }

      const res = await erpFetch("/scheme_plan", {
        method: "POST",
        body: payload,
      });

      if (isErpApiSuccess(res)) {
        toast.success(editRow ? "Plan updated successfully." : "Plan created successfully.");
        setShowModal(false);
        fetchList(true);
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const isFlexible = form.installment_type === "FLEXIBLE";

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""}`}>
          {/* Card Header */}
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h4 className="card-title mb-0">Scheme Plans</h4>
            </div>
            <div className="d-flex gap-2">
              {canAdd && (
                <button
                  id="scheme-plan-add-btn"
                  type="button"
                  className={`btn btn-success ${buttonClass}`}
                  onClick={openAdd}
                >
                  <Icon icon="solar:add-circle-line-duotone" className="me-1" width={18} />
                  Add Plan
                </button>
              )}
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-4">
            {/* Search Section */}
            <div className="d-flex justify-content-between mb-3 align-items-center flex-wrap gap-2">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search plans..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Metal</th>
                    <th>Value Mode</th>
                    <th>Duration</th>
                    <th>Installment</th>
                    <th>Default Amount</th>
                    <th>Late Fee</th>
                    <th>Mandal</th>
                    <th>Bonus</th>
                    <th>Status</th>
                    {canEdit && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12} className="text-center py-5 text-muted">
                        Loading scheme plans...
                      </td>
                    </tr>
                  ) : list.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center py-5 text-muted">
                        No scheme plans found.
                      </td>
                    </tr>
                  ) : (
                    list.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="fw-semibold text-dark">{row.name}</span>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {row.code || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-primary-subtle text-primary">
                            {row.metal_type}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted small">
                            {row.value_mode === "GOLD_WEIGHT" ? "Gold Weight (gm)" : "Amount (₹)"}
                          </span>
                        </td>
                        <td>
                          <span className="fw-medium">{row.duration_months} Months</span>
                        </td>
                        <td>
                          <span className={`badge ${row.installment_type === "FIXED" ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"}`}>
                            {row.installment_type}
                          </span>
                        </td>
                        <td>
                          {row.default_amount != null
                            ? formatIndianRupee(row.default_amount)
                            : "—"}
                        </td>
                        <td>
                          {row.late_fee_amount != null
                            ? formatIndianRupee(row.late_fee_amount)
                            : "—"}
                        </td>
                        <td>
                          {Number(row.is_mandal) === 1 ? (
                            <span className="badge bg-warning-subtle text-warning">Mandal</span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          {row.bonus_type === "FREE_MONTH" && (
                            <span className="text-success small fw-medium">
                              +{row.bonus_months} Free Mo
                            </span>
                          )}
                          {row.bonus_type === "PERCENT" && (
                            <span className="text-success small fw-medium">
                              +{row.bonus_percent}%
                            </span>
                          )}
                          {row.bonus_type === "NONE" && (
                            <span className="text-muted small">None</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              row.status === 1
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {row.status === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {canEdit && (
                          <td className="text-end">
                            <button
                              type="button"
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              onClick={() => openEdit(row)}
                              title="Edit Plan"
                            >
                              <Icon icon="solar:pen-bold" width={16} height={16} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <Pagination
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                currentPage={pagination.page}
                onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
                onItemsPerPageChange={(l) =>
                  setPagination((prev) => ({ ...prev, limit: l, page: 1 }))
                }
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Plan Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        backdrop="static"
        contentClassName={cardClass}
      >
        <Modal.Header closeButton className="border-bottom py-3 px-4">
          <Modal.Title className="fs-5 fw-semibold">
            {editRow ? "Edit Scheme Plan" : "Add Scheme Plan"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="p-4">
            <h6 className="fw-semibold text-primary mb-3">1. Basic Details</h6>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Plan Name *</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="e.g. Swarna Labham 11+1"
                    className={buttonClass}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Plan Code</Form.Label>
                  <Form.Control
                    value={form.code}
                    onChange={(e) => setField("code", e.target.value)}
                    placeholder="e.g. SL-11"
                    className={buttonClass}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Metal Type</Form.Label>
                  <Form.Select
                    value={form.metal_type}
                    onChange={(e) => setField("metal_type", e.target.value)}
                    className={buttonClass}
                  >
                    {METAL_TYPES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Value Mode</Form.Label>
                  <Form.Select
                    value={form.value_mode}
                    onChange={(e) => setField("value_mode", e.target.value)}
                    className={buttonClass}
                  >
                    {VALUE_MODES.map((v) => (
                      <option key={v} value={v}>
                        {v === "AMOUNT" ? "Amount (₹)" : "Gold Weight (gm)"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Duration (Months) *</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.duration_months}
                    onChange={(e) => setField("duration_months", e.target.value)}
                    placeholder="e.g. 11"
                    className={buttonClass}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Waiting Months</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={form.waiting_months}
                    onChange={(e) => setField("waiting_months", e.target.value)}
                    placeholder="e.g. 0"
                    className={buttonClass}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="fw-semibold text-primary mb-3">2. Installment & Values</h6>
            <Row className="g-3 mb-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Installment Type</Form.Label>
                  <Form.Select
                    value={form.installment_type}
                    onChange={(e) => setField("installment_type", e.target.value)}
                    className={buttonClass}
                  >
                    {INSTALLMENT_TYPES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Default Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.default_amount}
                    onChange={(e) => setField("default_amount", e.target.value)}
                    placeholder="e.g. 2000"
                    className={buttonClass}
                  />
                </Form.Group>
              </Col>

              {isFlexible ? (
                <>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Min Amount (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.min_amount}
                        onChange={(e) => setField("min_amount", e.target.value)}
                        placeholder="e.g. 500"
                        className={buttonClass}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Max Amount (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.max_amount}
                        onChange={(e) => setField("max_amount", e.target.value)}
                        placeholder="e.g. 50000"
                        className={buttonClass}
                      />
                    </Form.Group>
                  </Col>
                </>
              ) : (
                <Col md={4}></Col>
              )}
            </Row>

            <h6 className="fw-semibold text-primary mb-3">3. Bonus & Rules</h6>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bonus Type</Form.Label>
                  <Form.Select
                    value={form.bonus_type}
                    onChange={(e) => setField("bonus_type", e.target.value)}
                    className={buttonClass}
                  >
                    {BONUS_TYPES.map((v) => (
                      <option key={v} value={v}>
                        {v === "FREE_MONTH"
                          ? "Free Month(s)"
                          : v === "PERCENT"
                          ? "Percentage (%)"
                          : "None"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.bonus_type === "FREE_MONTH" && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Bonus Months</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={form.bonus_months}
                      onChange={(e) => setField("bonus_months", e.target.value)}
                      placeholder="e.g. 1"
                      className={buttonClass}
                    />
                  </Form.Group>
                </Col>
              )}
              {form.bonus_type === "PERCENT" && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Bonus %</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.bonus_percent}
                      onChange={(e) => setField("bonus_percent", e.target.value)}
                      placeholder="e.g. 8.33"
                      className={buttonClass}
                    />
                  </Form.Group>
                </Col>
              )}

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Late Fee Amount (₹){Number(form.is_mandal) === 1 ? " *" : ""}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.late_fee_amount}
                    onChange={(e) => setField("late_fee_amount", e.target.value)}
                    placeholder="e.g. 100"
                    className={buttonClass}
                    required={Number(form.is_mandal) === 1}
                  />
                  <div className="text-muted small mt-1">
                    Entry amount when hafta/loan is paid after due date (not cash-collected).
                  </div>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Mandal Plan</Form.Label>
                  <Form.Select
                    value={form.is_mandal}
                    onChange={(e) => setField("is_mandal", Number(e.target.value))}
                    className={buttonClass}
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes (Mandal)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Status</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                    className={buttonClass}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              className={buttonClass}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className={buttonClass}
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : editRow ? "Save Changes" : "Create Plan"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default SchemePlan;
